import { getReportTypeLabel, OTHER_REPORT_TYPE } from "../constants/reportTypes";

export type ReportResultTone = "normal" | "abnormal" | "neutral";

/** Formats a report date as "4 June 2026" (en-GB), matching the Notes date style without time. */
export function formatReportDate(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Human-readable label for a report type key. */
export function formatReportTypeLabel(
  reportType: string,
  reportTypeLabel?: string
): string {
  if (reportType === OTHER_REPORT_TYPE && reportTypeLabel?.trim()) {
    return reportTypeLabel.trim();
  }
  const known = getReportTypeLabel(reportType);
  if (known) {
    return known;
  }
  return reportType.toUpperCase().replace(/_/g, " ");
}

function parseNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function isCrpNormal(value: unknown): boolean {
  const n = parseNum(value);
  if (n == null) return true;
  return n >= 0 && n <= 5;
}

function isTropNormal(value: unknown): boolean {
  const n = parseNum(value);
  if (n == null) return true;
  return n < 26;
}

function isLipidFieldNormal(value: unknown, field: string): boolean {
  const n = parseNum(value);
  if (n == null) return true;
  switch (field) {
    case "hdl":
      return n >= 50;
    case "ldl":
      return n < 70;
    case "hdl_ratio":
      return n < 4;
    case "total_cholesterol":
      return n < 200;
    default:
      return true;
  }
}

function isCbcFieldNormal(value: unknown, field: string): boolean {
  const n = parseNum(value);
  if (n == null) return true;
  switch (field) {
    case "hemoglobin":
      return n >= 12 && n <= 15;
    case "wbc":
      return n >= 4000 && n <= 10000;
    case "platelets":
      return n >= 150000 && n <= 410000;
    default:
      return true;
  }
}

/** Plain-text summary for the result segment of a report row. */
export function getReportResultText(
  reportType: string,
  data: Record<string, unknown> | null | undefined
): string | null {
  if (!data) return null;

  switch (reportType) {
    case "crp": {
      const { crp_value, unit } = data;
      if (crp_value == null || crp_value === "") return null;
      const unitStr = unit ? ` ${unit}` : "";
      return `Result: ${crp_value}${unitStr}`;
    }
    case "trop_i": {
      const { troponin_i_value, unit } = data;
      if (troponin_i_value == null || troponin_i_value === "") return null;
      const unitStr = unit ? ` ${unit}` : "";
      return `Result: ${troponin_i_value}${unitStr}`;
    }
    case "ecg": {
      const impression = data.ecg_impression;
      if (impression == null || impression === "") return null;
      return String(impression);
    }
    case "cbc": {
      const { hemoglobin_g_dl, wbc_per_ul, platelets_per_ul } = data;
      const parts: string[] = [];
      if (hemoglobin_g_dl != null && hemoglobin_g_dl !== "")
        parts.push(`Hemoglobin: ${hemoglobin_g_dl}`);
      if (wbc_per_ul != null && wbc_per_ul !== "")
        parts.push(`WBC: ${wbc_per_ul}`);
      if (platelets_per_ul != null && platelets_per_ul !== "")
        parts.push(`Platelet: ${platelets_per_ul}`);
      return parts.length ? parts.join(", ") : null;
    }
    case "lipid_profile": {
      const {
        total_cholesterol,
        ldl_cholesterol,
        hdl_cholesterol,
        hdl_ratio,
      } = data;
      const parts: string[] = [];
      if (total_cholesterol != null && total_cholesterol !== "")
        parts.push(`Cholesterol total: ${total_cholesterol}`);
      if (ldl_cholesterol != null && ldl_cholesterol !== "")
        parts.push(`LDL: ${ldl_cholesterol}`);
      if (hdl_cholesterol != null && hdl_cholesterol !== "")
        parts.push(`HDL: ${hdl_cholesterol}`);
      if (hdl_ratio != null && hdl_ratio !== "")
        parts.push(`HDL ratio: ${hdl_ratio}`);
      return parts.length ? parts.join(", ") : null;
    }
    default:
      return null;
  }
}

/** Row-level tone: abnormal if any measured value is out of range; ECG stays neutral. */
export function getReportResultTone(
  reportType: string,
  data: Record<string, unknown> | null | undefined
): ReportResultTone {
  if (!data) return "neutral";

  switch (reportType) {
    case "ecg":
    case "chart":
      return "neutral";
    case "crp":
      return isCrpNormal(data.crp_value) ? "normal" : "abnormal";
    case "trop_i":
      return isTropNormal(data.troponin_i_value) ? "normal" : "abnormal";
    case "cbc": {
      const checks = [
        isCbcFieldNormal(data.hemoglobin_g_dl, "hemoglobin"),
        isCbcFieldNormal(data.wbc_per_ul, "wbc"),
        isCbcFieldNormal(data.platelets_per_ul, "platelets"),
      ];
      if (checks.every(Boolean)) return "normal";
      if (checks.some((c) => !c)) return "abnormal";
      return "neutral";
    }
    case "lipid_profile": {
      const checks = [
        isLipidFieldNormal(data.total_cholesterol, "total_cholesterol"),
        isLipidFieldNormal(data.ldl_cholesterol, "ldl"),
        isLipidFieldNormal(data.hdl_cholesterol, "hdl"),
        isLipidFieldNormal(data.hdl_ratio, "hdl_ratio"),
      ];
      const hasValues =
        data.total_cholesterol != null ||
        data.ldl_cholesterol != null ||
        data.hdl_cholesterol != null ||
        data.hdl_ratio != null;
      if (!hasValues) return "neutral";
      if (checks.every(Boolean)) return "normal";
      return "abnormal";
    }
    default:
      return "neutral";
  }
}

/** Tailwind class for result text from tone. */
export function getReportResultToneClassName(tone: ReportResultTone): string {
  switch (tone) {
    case "abnormal":
      return "text-vital-high";
    case "normal":
      return "text-report-normal";
    default:
      return "text-guardian-body";
  }
}
